import { ACTIVE_BACKEND } from 'app/App'
import SFAuthService, { NO_USER } from 'app/services/sfAuth/SFAuthService'
import { mapSFToForm, reverseMap } from 'app/services/sfAuth/sfDataService'
import { isString } from 'lodash'
import { AccountTeamMember } from './salesforce-maps/AccountTeamMember'
import { Action } from './salesforce-maps/Action'
import { Application } from './salesforce-maps/Application'
import { ApplicationTeamMember } from './salesforce-maps/ApplicationTeamMember'
import { Case } from './salesforce-maps/Case'
import { Configuration } from './salesforce-maps/Configuration'
import { ContentDocumentLink } from './salesforce-maps/ContentDocumentLink'
import { ContentVersion } from './salesforce-maps/ContentVersion'

import { DescribeQueryResult } from './salesforce-maps/DescribeTypes'
import { ErrorLog } from './salesforce-maps/ErrorLog'
import { ExternalApproval } from './salesforce-maps/ExternalApproval'
import { FormPage } from './salesforce-maps/FormPage'
import { FundingProgram } from './salesforce-maps/FundingProgram'
import { FundingStream } from './salesforce-maps/FundingStream'
import { GranteeBudgetCategory } from './salesforce-maps/GranteeBudgetCategory'
import { GranteeReport } from './salesforce-maps/GranteeReport'
import { MyShelf } from './salesforce-maps/MyShelf'
import { Network } from './salesforce-maps/Network'
import { ReportRecordTypes } from './salesforce-maps/ReportRecordTypes'
import { ReviewAssignement } from './salesforce-maps/ReviewAssignement'
import { FieldMap, fieldMapsByType, IncludeRelationshipFields, objectTypeMappingForSalesforce, typeMappingForIncludeRelationships } from './salesforce-maps/SFObjectsMapping'
import { SurveyText } from './salesforce-maps/SurveyText'
import { SurveyTranslation } from './salesforce-maps/SurveyTranslation'
import { Account, ICCEAccount, accountFieldsMapSF, ICCEAccountFieldsMapSF } from './salesforce-maps/Account'
import sfOauthConfig from '../services/sfAuth/sfAuthConfig'

export const NO_PARSER_ERROR = 'No parser function configured for requested query!'
export const NO_TYPE_MAPPING_CONFIGURED = 'No backend mapping configured for provided type!'
export const NO_ID_FOR_UPDATE = 'No id passed for update query!'
export const NO_ID_FOR_DELETE = 'No id passed for delete query!'

type Backend = 'Salesforce'

interface Parsers<K extends keyof TypeMap> {
  in: (data: any) => TypeMap[K]
  out: (data: TypeMap[K] | CreateData<K>) => any
}

const entries = Object.entries(fieldMapsByType)

entries.push(['Account', null as any])

export const backendParsers = Object.fromEntries(
  entries.map(([key, fieldMap]) => {
    const isIcce = Boolean(sfOauthConfig.isIcce)

    const getFieldMap = (): FieldMap =>
      key === 'Account'
        ? (isIcce
            ? ICCEAccountFieldsMapSF
            : accountFieldsMapSF)
        : fieldMap

    return [
      key,
      {
        Salesforce: {
          in: (obj: any) => mapSFToForm(getFieldMap(), obj),
          out: (obj: any) =>
            mapSFToForm(
              reverseMap(getFieldMap()),
              obj,
              key === 'Error Log'
            )
        }
      }
    ]
  })
) as Record<keyof TypeMap, Record<Backend, Parsers<keyof TypeMap>>>

export interface TypeMap {
  'Network': Network
  'My Shelf': MyShelf
  'Content Version': ContentVersion
  'Content Document Link': ContentDocumentLink
  Configuration: Configuration
  'Error Log': ErrorLog
  'Action': Action
  'SurveyTranslation': SurveyTranslation
  'SurveyText': SurveyText
  'FormPage': FormPage
  'Funding Stream': FundingStream
  'Funding Program': FundingProgram
  'Grantee Budget Category': GranteeBudgetCategory
  'Grantee Report': GranteeReport
  'Report Record Types': ReportRecordTypes
  'Application': Application
  'Application Team Member': ApplicationTeamMember
  Case: Case
  'Review Assignement': ReviewAssignement
  'Account Team Member': AccountTeamMember
  'External Approval': ExternalApproval
  'Account': Account | ICCEAccount
}

interface DeleteObjectProps {
  type: keyof TypeMap
  ids: string | string[]
}

export interface UpdateQueryResultSalesforce {
  errors: any[]
  id: string
  success: boolean
}

interface UpdateObjectProps<K extends keyof TypeMap> {
  type: K
  updateData: Partial<TypeMap[K]> | Array<Partial<TypeMap[K]>>
}

type CreateData<K extends keyof TypeMap> = Omit<TypeMap[K], 'id'>

interface CreateObjectProps<K extends keyof TypeMap> {
  type: K
  createData: Partial<CreateData<K>> | Array<Partial<CreateData<K>>>
}

type StrictIncludeBase<K extends keyof TypeMap> = K extends keyof typeof typeMappingForIncludeRelationships
  ? typeof typeMappingForIncludeRelationships[K] extends ReadonlyArray<infer T>
    ? T
    : never
  : never

type IncludeBase<K extends keyof TypeMap> = [StrictIncludeBase<K>] extends [never]
  ? string
  : StrictIncludeBase<K>

type SelectFieldsForInclude<B extends string> = B extends keyof IncludeRelationshipFields
  ? Array<IncludeRelationshipFields[B & keyof IncludeRelationshipFields] & string>
  : string[]

type IncludeDefinition<K extends keyof TypeMap> = {
  [B in IncludeBase<K>]: {
    base: B
    select?: SelectFieldsForInclude<B>
  }
}[IncludeBase<K>]

interface GetObjectsProps<K extends keyof TypeMap> {
  maxFetch?: number
  type: K
  select?: string[]
  include?: Array<IncludeBase<K> | IncludeDefinition<K>>
  searchProps?: Record<string, any>
}

interface GetObjectProps<K extends keyof TypeMap = keyof TypeMap> extends Omit<GetObjectsProps<K>, 'searchProps'> {
  id: string
}

interface DescribeObjectProps {
  type: keyof TypeMap
}

const commonBackendFunctionalities = {
  Salesforce: {
    get: async <K extends keyof TypeMap>({ type, maxFetch = 1000, select, include, searchProps = {} }: GetObjectsProps<K>): Promise<Array<TypeMap[K]>> => {
      const conn = SFAuthService.getConnection()
      const sfType = objectTypeMappingForSalesforce[type]
      if (conn == null) {
        return await Promise.reject(new Error(NO_USER))
      }
      if (sfType == null) {
        return await Promise.reject(new Error(NO_TYPE_MAPPING_CONFIGURED + ` ${type}`))
      }
      let query = conn.sobject(sfType).find(searchProps)

      if (select != null) {
        // TODO do object field name mapping
        query = query.select(select.join(', '))
      }
      if (include != null) {
        // TODO do object nae mapping for include base
        include.forEach(def => {
          if (isString(def)) {
            query = query.include(def)
          } else {
            query = query.include(def.base)
            if (def.select != null) {
              query = query.select(def.select.join(', '))
            } else {
              query = query.select('*')
            }
          }
          query = query.end()
        })
      }

      return query.run({ autoFetch: true, maxFetch })
        .then(async (result) => {
          const parser = backendParsers[type][ACTIVE_BACKEND]
          if (parser == null) {
            return await Promise.reject(new Error(NO_PARSER_ERROR))
          }
          return result.map(obj => parser.in(obj))
        })
    },
    update: async <K extends keyof TypeMap>({ type, updateData }: UpdateObjectProps<K>):
    Promise<UpdateObjectProps<K>['updateData'] extends Array<TypeMap[K]>
      ? UpdateQueryResultSalesforce[]
      : UpdateQueryResultSalesforce> => {
      const conn = SFAuthService.getConnection()
      const sfType = objectTypeMappingForSalesforce[type]
      if (conn == null) {
        return await Promise.reject(new Error(NO_USER))
      }
      if (sfType == null) {
        return await Promise.reject(new Error(NO_TYPE_MAPPING_CONFIGURED + ` ${type}`))
      }
      const mappedData = Array.isArray(updateData)
        ? updateData.map(chunk => backendParsers[type][ACTIVE_BACKEND].out(chunk))
        : backendParsers[type][ACTIVE_BACKEND].out(updateData)

      if (Array.isArray(mappedData)) {
        const noId = mappedData.some(obj => !('Id' in obj))
        if (noId) {
          return await Promise.reject(new Error(NO_ID_FOR_UPDATE))
        }
      } else if (!('Id' in mappedData)) {
        return await Promise.reject(new Error(NO_ID_FOR_UPDATE))
      }
      return conn
        .sobject(sfType)
        .update(mappedData)
    },
    delete: async <T extends DeleteObjectProps>({ type, ids }: DeleteObjectProps): Promise<T['ids'] extends string[] ? never[] : never> => {
      const conn = SFAuthService.getConnection()
      const sfType = objectTypeMappingForSalesforce[type]
      if (conn == null) {
        return await Promise.reject(new Error(NO_USER))
      }
      if (sfType == null) {
        return await Promise.reject(new Error(NO_TYPE_MAPPING_CONFIGURED + ` ${type}`))
      }
      if (ids == null || (Array.isArray(ids) && ids.length === 0)) {
        return await Promise.reject(new Error(NO_ID_FOR_DELETE))
      }
      return conn
        .sobject(sfType)
        .delete(ids)
    },
    create: async <K extends keyof TypeMap>({ type, createData }: CreateObjectProps<K>):
    Promise<CreateObjectProps<K>['createData'] extends Array<CreateData<K>>
      ? UpdateQueryResultSalesforce[]
      : UpdateQueryResultSalesforce> => {
      const conn = SFAuthService.getConnection()
      const sfType = objectTypeMappingForSalesforce[type]
      if (conn == null) {
        return await Promise.reject(new Error(NO_USER))
      }
      if (sfType == null) {
        return await Promise.reject(new Error(NO_TYPE_MAPPING_CONFIGURED))
      }
      const mappedData = Array.isArray(createData)
        ? createData.map(chunk => backendParsers[type][ACTIVE_BACKEND].out(chunk))
        : backendParsers[type][ACTIVE_BACKEND].out(createData)
      return conn
        .sobject(sfType)
        .create(mappedData)
    },
    describe: async <T extends DescribeObjectProps>({ type }: T): Promise<DescribeQueryResult> => {
      const conn = SFAuthService.getConnection()
      const sfType = objectTypeMappingForSalesforce[type]
      if (conn == null) {
        return await Promise.reject(new Error(NO_USER))
      }
      if (sfType == null) {
        return await Promise.reject(new Error(NO_TYPE_MAPPING_CONFIGURED))
      }
      return conn
        .sobject(sfType)
        .describe()
    }
  }
}

export async function queryCreateObject<K extends keyof TypeMap> (
  props: CreateObjectProps<K>
): Promise<
  CreateObjectProps<K>['createData'] extends Array<CreateData<K>>
    ? UpdateQueryResultSalesforce[]
    : UpdateQueryResultSalesforce
  > {
  return await commonBackendFunctionalities[ACTIVE_BACKEND].create<K>(props)
}

export async function queryGetObject<K extends keyof TypeMap> (props: GetObjectProps<K>): Promise<TypeMap[K]> {
  const result = await queryGetObjects({
    select: props.select,
    searchProps: {
      Id: props.id
    },
    maxFetch: props.maxFetch,
    type: props.type,
    ...(props.include != null ? { include: props.include } : {})
  })
  return result[0]
}

export async function queryGetObjects<K extends keyof TypeMap> (props: GetObjectsProps<K>): Promise<Array<TypeMap[K]>> {
  return await commonBackendFunctionalities[ACTIVE_BACKEND].get(props)
}

export async function queryUpdateObject<K extends keyof TypeMap> (props: UpdateObjectProps<K>): Promise<
UpdateObjectProps<K>['updateData'] extends Array<TypeMap[K]>
  ? UpdateQueryResultSalesforce[]
  : UpdateQueryResultSalesforce
> {
  return await commonBackendFunctionalities[ACTIVE_BACKEND].update<K>(props)
}

export async function queryDeleteObject<T extends DeleteObjectProps> (props: T): Promise<T['ids'] extends string[] ? UpdateQueryResultSalesforce[] : UpdateQueryResultSalesforce> {
  return await commonBackendFunctionalities[ACTIVE_BACKEND].delete<T>(props)
}

export async function queryDescribeObject<T extends DescribeObjectProps> (props: T): Promise<DescribeQueryResult> {
  return await commonBackendFunctionalities[ACTIVE_BACKEND].describe(props)
}
